import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { Inbox, Loader2, Plus, Send, Unlink } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { getAgencies, getTelnyxFaxLine } from '../../services/api';
import {
  describeExtension,
  claimableExtensions,
  duplicateFaxNumbers,
  linkFaxNumber,
  listAgencyFaxNumbers,
  listRingCentralInventory,
  provisionFaxNumber,
  setFaxNumberMappingActive,
  unclaimedExtensions,
  type AgencyFaxNumber,
  type AgencyFaxNumbers,
  type RingCentralInventory,
} from '../../services/faxNumbers';
import {
  listAgencyClinics,
  type AgencyClinicOption,
} from '../../services/unassignedFaxes';
import { sendFax } from '../../services/outboundFaxes';
import { deliverDirectFax } from '../../services/directFaxes';
import { inferDeployStageLabel } from '../../utils/deployStage';
import {
  extractDetailMessage,
  getBusinessErrorMessage,
  getResponseDetail,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';

// The API refuses a direct fax in production whatever its configuration says,
// so the button would be one that can only fail there. Hidden rather than
// disabled: there is nothing an operator could do to make it work.
const CAN_DIRECT_FAX = inferDeployStageLabel() !== 'prod';

/** E.164, matching the server so a typo costs no round trip. */
const E164 = /^\+[1-9][0-9]{7,14}$/;

const providerName = (provider: string) =>
  provider === 'telnyx' ? 'Telnyx' : provider;

/** What a mapping routes on, in words: an extension, or a dialled number. */
const routeOf = (mapping: AgencyFaxNumber) =>
  mapping.provider === 'ringcentral'
    ? `extension ${mapping.ringcentral_extension_id}`
    : `${providerName(mapping.provider)} number ${mapping.routing_key}`;

/**
 * The fax numbers routed to each agency, and a way to buy one more.
 *
 * Deliberately a list per agency rather than a has-one flag. Only the
 * RingCentral extension is unique, so an agency can hold several numbers: one
 * per branch for a multi-site agency, or several ported in from a carrier. The
 * optional clinic on each is how an agency narrows a number to the site that
 * owns it; left unset the whole agency sees those faxes.
 */
interface FaxNumbersPanelProps {
  /**
   * Called whenever this panel changes what is sitting in the parked queue.
   *
   * Two things here do: linking a number can collect a backlog, and placing a
   * direct fax with no agency adds to it. The queue is a force-mounted sibling
   * that never remounts, so it has to be told either way.
   */
  onParkedFaxesChanged?: () => void;
}

export const FaxNumbersPanel: React.FC<FaxNumbersPanelProps> = ({
  onParkedFaxesChanged,
}) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [agencies, setAgencies] = useState<AgencyFaxNumbers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');

  // The agency being added to. `agencyId` is null when the dialog was opened
  // from the header rather than from a row, which is the only way to give an
  // agency its FIRST number: an agency with none has no row to click.
  const [adding, setAdding] = useState<{
    agencyId: string | null;
    agencyName: string | null;
  } | null>(null);
  const [allAgencies, setAllAgencies] = useState<
    { id: string; name: string }[]
  >([]);
  const [chosenAgencyId, setChosenAgencyId] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [label, setLabel] = useState('');
  const [clinics, setClinics] = useState<AgencyClinicOption[]>([]);
  const [clinicId, setClinicId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [inventory, setInventory] = useState<RingCentralInventory | null>(null);
  const [inventoryFailed, setInventoryFailed] = useState(false);
  const [extensionId, setExtensionId] = useState('');
  // Whether to also collect what is already parked on the chosen extension.
  // Starts off every time the dialog opens: placing a fax cannot be undone.
  const [claimWaiting, setClaimWaiting] = useState(false);

  // The mapping whose routing is being changed, with the agency it names, so
  // the confirmation can say what stops arriving where.
  const [unlinking, setUnlinking] = useState<{
    mapping: AgencyFaxNumber;
    agencyName: string;
  } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  // The server refuses to unlink the outbound number of an agency sending
  // through Telnyx: 'live' when it is one, 'unknown' when that could not be
  // checked, so the dialog never promises an unlink that will be refused.
  const [outboundCheck, setOutboundCheck] = useState<
    'none' | 'checking' | 'live' | 'unknown'
  >('none');

  const [isDirectOpen, setIsDirectOpen] = useState(false);
  const [directAgencyId, setDirectAgencyId] = useState('');
  const [directClinicId, setDirectClinicId] = useState('');
  const [directClinics, setDirectClinics] = useState<AgencyClinicOption[]>([]);
  const [directFrom, setDirectFrom] = useState('');
  const [directSubject, setDirectSubject] = useState('');
  const [directExtension, setDirectExtension] = useState('');
  const [directFiles, setDirectFiles] = useState<File[]>([]);
  const [isDelivering, setIsDelivering] = useState(false);

  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendFiles, setSendFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      setAgencies(await listAgencyFaxNumbers());
    } catch (error) {
      console.error('Failed to load fax numbers:', error);
      setAgencies([]);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Computed across every agency, not within one: the same number typed against
  // two different agencies is the mistake worth catching.
  const duplicates = useMemo(() => duplicateFaxNumbers(agencies), [agencies]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return agencies;
    return agencies.filter(agency =>
      agency.agency_name.toLowerCase().includes(needle)
    );
  }, [agencies, search]);

  const openAdd = async (agency: AgencyFaxNumbers | null) => {
    setAdding({
      agencyId: agency?.agency_id ?? null,
      agencyName: agency?.agency_name ?? null,
    });
    setAreaCode('');
    setLabel('');
    setClinicId('');
    setClinics([]);
    setChosenAgencyId('');
    setExtensionId('');
    setClaimWaiting(false);
    setInventory(null);
    setInventoryFailed(false);

    // Only needed when no agency is preset. Every agency, not only those with
    // a number, because the whole point of this entry point is the ones
    // without.
    if (!agency) {
      setAllAgencies([]);
      void getAgencies({ query: { limit: 1000 }, throwOnError: true })
        .then(response =>
          setAllAgencies(
            (response.data ?? []).map(entry => ({
              id: entry.id,
              name: entry.system_name,
            }))
          )
        )
        .catch((error: unknown) => {
          console.error('Failed to load agencies:', error);
          showError('Could not load agencies', 'Reopen this to try again.');
        });
    }

    if (agency) loadClinicsFor(agency.agency_id);

    try {
      setInventory(await listRingCentralInventory());
    } catch (error) {
      console.error('Failed to read the fax provider account:', error);
      // Deliberately not an empty picker. Empty reads as "nothing exists
      // yet", which invites a second extension for an agency that has one.
      setInventoryFailed(true);
    }
  };

  const loadClinicsFor = (agencyId: string) => {
    setClinicId('');
    setClinics([]);
    // Independent of the inventory load, and neither should hide the other's
    // failure: clinics come from our own database, the inventory from
    // RingCentral.
    void listAgencyClinics(agencyId)
      .then(setClinics)
      .catch((error: unknown) => {
        console.error('Failed to load clinics:', error);
        // Not fatal: a number with no clinic is visible agency wide, which is
        // the right default anyway.
        showError(
          'Could not load clinics',
          'You can still add an agency number.'
        );
      });
  };

  const confirmLink = async () => {
    const chosen = claimableExtensions(inventory).find(
      extension => extension.extension_id === extensionId
    );
    if (!chosen || !targetAgencyId) return;
    setIsSaving(true);
    try {
      const linked = await linkFaxNumber({
        ringcentral_extension_id: chosen.extension_id,
        agency_id: targetAgencyId,
        agency_clinic_id: clinicId || null,
        // Taken from the listing rather than typed, so the number always
        // belongs to the extension it is recorded against. Withheld entirely
        // while a port is running: the number showing then is a temporary one
        // RingCentral swaps out at cutover, so recording it would leave the
        // agency labelled with a number that stopped being theirs, and would
        // hold a slot in the unique index on this column. Routing does not
        // need it, because a fax carries the extension.
        fax_number:
          portingOnChosen.length > 0 ? null : (chosen.fax_numbers[0] ?? null),
        label: label.trim() || null,
        // Guarded rather than trusted: the checkbox only renders while the
        // chosen extension has a backlog, so sending the raw flag could move
        // faxes for an extension whose checkbox was never shown. Moving them
        // cannot be undone, which is the whole reason for asking.
        claim_waiting_faxes: claimWaiting && waitingOnChosen > 0,
      });
      showSuccess(
        'Number linked',
        linked.claimed_fax_count > 0
          ? `Faxes to it reach that agency now, and ${linked.claimed_fax_count} that were waiting moved with it.`
          : 'Faxes to it reach that agency now.'
      );
      setAdding(null);
      // Only when it actually took some: the sibling queue has nothing to
      // reload otherwise.
      if (linked.claimed_fax_count > 0) onParkedFaxesChanged?.();
      await load();
    } catch (error) {
      console.error('Failed to link a fax number:', error);
      // The server names the reason when it has one, such as an agency
      // without eFax, which a reload would never reveal.
      showError(
        'Could not link that number',
        getBusinessErrorMessage(error) ??
          'It may already be routed to an agency. Reload and check.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirmAdd = async () => {
    if (!targetAgencyId) return;
    setIsSaving(true);
    try {
      await provisionFaxNumber({
        agency_id: targetAgencyId,
        area_code: areaCode.trim(),
        agency_clinic_id: clinicId || null,
        label: label.trim() || null,
      });
      showSuccess('Number added', 'It is routed to that agency now.');
      setAdding(null);
      await load();
    } catch (error) {
      console.error('Failed to provision a fax number:', error);
      // The expected answer today: buying is off in every deployment until
      // somebody turns it on, so say that rather than a generic failure.
      showError(
        'Could not get a number',
        'This deployment may not be allowed to buy numbers. Check the logs.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openUnlink = async (
    mapping: AgencyFaxNumber,
    agency: AgencyFaxNumbers
  ) => {
    setUnlinking({ mapping, agencyName: agency.agency_name });
    if (mapping.provider !== 'telnyx' || !mapping.is_active) {
      setOutboundCheck('none');
      return;
    }
    setOutboundCheck('checking');
    try {
      const { data: line } = await getTelnyxFaxLine({
        path: { agency_id: agency.agency_id },
        throwOnError: true,
      });
      setOutboundCheck(
        line.sending_via === 'telnyx' && line.outbound_mapping_id === mapping.id
          ? 'live'
          : 'none'
      );
    } catch (error) {
      console.error('Failed to read the Telnyx fax line:', error);
      setOutboundCheck('unknown');
    }
  };

  const confirmUnlink = async () => {
    if (!unlinking) return;
    const { mapping } = unlinking;
    const next = !mapping.is_active;
    setIsUnlinking(true);
    try {
      await setFaxNumberMappingActive(mapping.id, next);
      if (next) {
        showSuccess('Number relinked', 'Faxes to it reach that agency again.');
      } else {
        showSuccess(
          'Number unlinked',
          'Faxes arriving on it now park in Unassigned Faxes.'
        );
      }
      setUnlinking(null);
      await load();
    } catch (error) {
      console.error('Failed to change a fax number mapping:', error);
      if (next) {
        // The only failure worth naming: both uniqueness indexes constrain
        // active rows, so reactivating can collide where deactivating cannot.
        showError(
          'Could not relink that number',
          'Another mapping may have taken that extension or number. Reload and check.'
        );
      } else {
        // A 409 names its own fix, such as changing a live outbound number.
        showError(
          'Could not unlink that number',
          (getHttpStatus(error) === 409 &&
            extractDetailMessage(getResponseDetail(error))) ||
            'Reload and try again.'
        );
      }
    } finally {
      setIsUnlinking(false);
    }
  };

  const openDirect = () => {
    setDirectAgencyId('');
    setDirectClinicId('');
    setDirectClinics([]);
    setDirectFrom('');
    setDirectSubject('');
    setDirectExtension('');
    setDirectFiles([]);
    setIsDirectOpen(true);
    // Every agency, not only those with a number: the whole point is to fill
    // an inbox on a stage where no number has ever been mapped.
    if (allAgencies.length === 0) {
      void getAgencies({ query: { limit: 1000 }, throwOnError: true })
        .then(response =>
          setAllAgencies(
            (response.data ?? []).map(entry => ({
              id: entry.id,
              name: entry.system_name,
            }))
          )
        )
        .catch((error: unknown) => {
          console.error('Failed to load agencies:', error);
          showError('Could not load agencies', 'Reopen this to try again.');
        });
    }
  };

  const confirmDirect = async () => {
    if (directFiles.length === 0) return;
    setIsDelivering(true);
    try {
      const placed = await deliverDirectFax(
        {
          agency_id: directAgencyId || null,
          agency_clinic_id: directClinicId || null,
          fax_number_from: directFrom.trim() || null,
          subject: directSubject.trim() || null,
          ringcentral_owner_id: directExtension.trim() || null,
        },
        directFiles
      );
      showSuccess(
        'Fax placed',
        directAgencyId
          ? `${placed.page_count} pages are in that agency's inbox now.`
          : `${placed.page_count} pages are waiting in Unassigned Faxes.`
      );
      // A placed fax with no agency lands in the parked queue, so the same
      // sibling needs telling.
      if (!directAgencyId) onParkedFaxesChanged?.();
      setIsDirectOpen(false);
    } catch (error) {
      console.error('Failed to place a fax:', error);
      // The expected answer in production, where this is refused whatever the
      // configuration says, so name that rather than a generic failure.
      showError(
        'Could not place that fax',
        'This deployment may not place faxes by hand. Check the logs.'
      );
    } finally {
      setIsDelivering(false);
    }
  };

  const openSend = () => {
    setSendTo('');
    setSendFiles([]);
    setIsSendOpen(true);
  };

  const confirmSend = async () => {
    if (sendFiles.length === 0) return;
    setIsSending(true);
    try {
      const sent = await sendFax(sendTo.trim(), sendFiles);
      // Queued, not delivered: RingCentral transmits afterwards at about a
      // minute a page, so say what actually happened.
      showSuccess(
        'Fax queued',
        `The fax provider accepted it as message ${sent.message_id}.`
      );
      setIsSendOpen(false);
    } catch (error) {
      console.error('Failed to send a fax:', error);
      showError(
        'Could not send the fax',
        'This deployment may not be allowed to send. Check the logs.'
      );
    } finally {
      setIsSending(false);
    }
  };

  // Either the row that opened the dialog, or the one picked inside it.
  const targetAgencyId = adding?.agencyId ?? (chosenAgencyId || '');
  const areaCodeLooksValid = /^[2-9][0-9]{2}$/.test(areaCode.trim());
  // Buying is refused by RingCentral itself today, so the dialog offers the
  // console route unless the account has actually been granted the permission.
  const canPurchase = inventory?.can_purchase ?? false;
  const available = claimableExtensions(inventory);
  // Offered only once an extension is chosen, and only when it has a backlog:
  // the count belongs to the extension, not to the agency.
  const waitingOnChosen =
    available.find(extension => extension.extension_id === extensionId)
      ?.waiting_fax_count ?? 0;
  // Numbers on the chosen extension that RingCentral has not settled yet,
  // which in practice means a port from another carrier is still running.
  const portingOnChosen =
    available.find(extension => extension.extension_id === extensionId)
      ?.porting_numbers ?? [];
  const canSend = E164.test(sendTo.trim()) && sendFiles.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle>Fax Numbers</CardTitle>
            <CardDescription>
              The numbers routed to each agency. An agency can have several, one
              per branch or several ported in. A number with no clinic is
              visible to the whole agency.
            </CardDescription>
          </div>
          <div className='flex shrink-0 flex-wrap gap-2'>
            {/* The only route to an agency's FIRST number: one with none has
                no row to click. */}
            <Button
              variant='outline'
              size='sm'
              onClick={() => void openAdd(null)}
            >
              <Plus className='mr-1 h-4 w-4' />
              Add a fax number
            </Button>
            <Button variant='outline' size='sm' onClick={openSend}>
              <Send className='mr-1 h-4 w-4' />
              Send a fax
            </Button>
            {/* The inbound counterpart, and the only way to fill an inbox on
                a stage RingCentral does not deliver to. */}
            {CAN_DIRECT_FAX && (
              <Button variant='outline' size='sm' onClick={openDirect}>
                <Inbox className='mr-1 h-4 w-4' />
                Direct fax
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : loadFailed ? (
          <div className='space-y-3 py-10 text-center'>
            <p className='text-destructive'>Could not load fax numbers.</p>
            <Button variant='outline' size='sm' onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : (
          <>
            <Input
              aria-label='Filter by agency'
              placeholder='Agency name'
              className='mb-3 w-64'
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
            {visible.length === 0 ? (
              <p className='py-10 text-center text-muted-foreground'>
                No agency has a fax number yet. Use Add a fax number above.
              </p>
            ) : (
              <div className='overflow-x-auto'>
                <Table className='min-w-[760px]'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agency</TableHead>
                      <TableHead>Number</TableHead>
                      <TableHead>Extension</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.map(agency =>
                      agency.numbers.map((number, index) => (
                        <TableRow key={number.id}>
                          <TableCell>
                            {/* Named once per agency so several numbers read
                                as one agency's block rather than repeats. */}
                            {index === 0 ? agency.agency_name : ''}
                          </TableCell>
                          <TableCell>
                            {number.fax_number ?? '—'}
                            {number.fax_number &&
                              duplicates.has(number.fax_number) && (
                                <Badge
                                  variant='destructive'
                                  className='ml-2 whitespace-nowrap'
                                >
                                  duplicate
                                </Badge>
                              )}
                            {!number.is_active && (
                              <Badge
                                variant='secondary'
                                className='ml-2 whitespace-nowrap'
                              >
                                inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* A Telnyx row holds its number in the extension
                                column too; it has no extension to show. */}
                            {number.provider === 'ringcentral' ? (
                              number.ringcentral_extension_id
                            ) : (
                              <Badge
                                variant='secondary'
                                className='whitespace-nowrap'
                              >
                                {providerName(number.provider)}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {number.clinic_name ?? 'Whole agency'}
                          </TableCell>
                          <TableCell className='text-right'>
                            <div className='flex justify-end gap-2'>
                              {/* Per row, unlike the add button: it acts on
                                  this mapping, not on the agency. */}
                              <Button
                                variant='outline'
                                size='sm'
                                aria-label={
                                  number.is_active
                                    ? `Unlink ${number.fax_number ?? number.ringcentral_extension_id}`
                                    : `Relink ${number.fax_number ?? number.ringcentral_extension_id}`
                                }
                                onClick={() => void openUnlink(number, agency)}
                              >
                                <Unlink className='mr-1 h-4 w-4' />
                                {number.is_active ? 'Unlink' : 'Relink'}
                              </Button>
                              {index === 0 && (
                                <Button
                                  variant='outline'
                                  size='sm'
                                  onClick={() => void openAdd(agency)}
                                >
                                  <Plus className='mr-1 h-4 w-4' />
                                  {/* Only ever shown for an agency that
                                      already has one, so say which it means.
                                      The header button covers the first. */}
                                  Add another number
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog
        open={adding !== null}
        onOpenChange={open => !open && setAdding(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {adding?.agencyName
                ? `Add a fax number for ${adding.agencyName}`
                : 'Add a fax number'}
            </DialogTitle>
            <DialogDescription>
              {canPurchase
                ? "This buys a number, which is a recurring monthly charge. Pick the area code the agency's referrers would expect to dial."
                : "Create the extension and buy its number in the fax provider's console first, then choose it here. Roger can read the account but not change it."}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            {adding && adding.agencyId === null && (
              <VirtualizedFormSelect
                label='Agency'
                placeholder='Choose an agency'
                options={allAgencies.map(agency => ({
                  value: agency.id,
                  label: agency.name,
                }))}
                sortOptions
                defaultValue={chosenAgencyId}
                onChange={event => {
                  setChosenAgencyId(event.target.value);
                  // The clinic list belongs to the agency, so it has to
                  // follow the choice rather than stay from the last one.
                  if (event.target.value) loadClinicsFor(event.target.value);
                  else setClinics([]);
                }}
              />
            )}
            {canPurchase ? (
              <Input
                aria-label='Area code'
                placeholder='415'
                maxLength={3}
                value={areaCode}
                onChange={event => setAreaCode(event.target.value)}
              />
            ) : inventoryFailed ? (
              <p className='text-sm text-destructive'>
                Could not read the fax provider account, so there is nothing
                safe to choose from. Try again shortly.
              </p>
            ) : inventory === null ? (
              <div className='flex justify-center py-4'>
                <Loader2 className='h-5 w-5 animate-spin' />
              </div>
            ) : available.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                {unclaimedExtensions(inventory).length > 0
                  ? // Different problem, different fix: the extension exists,
                    // it just has no number to receive on.
                    "No unclaimed extension has a number yet. Assign one in the fax provider's console, then reopen this."
                  : "Every extension on the account is already routed to an agency. Add one in the fax provider's console, then reopen this."}
              </p>
            ) : (
              <VirtualizedFormSelect
                key={`extension-${targetAgencyId || 'none'}`}
                label='Provider number'
                placeholder='Choose a number'
                options={available.map(extension => ({
                  value: extension.extension_id,
                  label: describeExtension(extension),
                }))}
                sortOptions
                defaultValue={extensionId}
                onChange={event => {
                  setExtensionId(event.target.value);
                  // The consent belongs to the extension it was given for.
                  // Carrying it to the next one would move that extension's
                  // backlog on a tick nobody made for it.
                  setClaimWaiting(false);
                }}
              />
            )}
            <Input
              aria-label='Label'
              placeholder='What this number is for (optional)'
              value={label}
              onChange={event => setLabel(event.target.value)}
            />
            {/* A port that has not finished. Worth saying plainly, because
                the number on screen is not the one the agency will end up
                with, and nothing else on this dialog hints at that. */}
            {portingOnChosen.length > 0 && (
              <p className='text-sm text-muted-foreground'>
                The fax provider is still porting{' '}
                <strong>{portingOnChosen.join(', ')}</strong>. That is a
                temporary number and it will change when the port completes, so
                this mapping records the extension only. Faxes still reach the
                agency throughout, because routing follows the extension.
              </p>
            )}
            {/* Only when there is a backlog to offer. A number ported in
                mid-flight receives before anybody maps it, and this is the
                moment somebody can say where those faxes belong. */}
            {waitingOnChosen > 0 && (
              <label className='flex items-start gap-2 text-sm'>
                <input
                  type='checkbox'
                  className='mt-1'
                  checked={claimWaiting}
                  onChange={event => setClaimWaiting(event.target.checked)}
                />
                <span>
                  Also move the{' '}
                  <strong>
                    {waitingOnChosen} fax{waitingOnChosen === 1 ? '' : 'es'}
                  </strong>{' '}
                  already waiting on this extension into this agency's inbox.
                  This cannot be undone.
                </span>
              </label>
            )}
            {clinics.length > 0 && (
              <VirtualizedFormSelect
                key={`clinic-${targetAgencyId || 'none'}`}
                label='Clinic (optional)'
                placeholder='Whole agency'
                options={clinics.map(clinic => ({
                  value: clinic.id,
                  label: clinic.name,
                }))}
                sortOptions
                defaultValue={clinicId}
                onChange={event => setClinicId(event.target.value)}
                isClearable
              />
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setAdding(null)}>
              Cancel
            </Button>
            {canPurchase ? (
              <Button
                onClick={() => void confirmAdd()}
                disabled={!areaCodeLooksValid || !targetAgencyId || isSaving}
              >
                {isSaving ? 'Getting a number...' : 'Get a number'}
              </Button>
            ) : (
              <Button
                onClick={() => void confirmLink()}
                disabled={!extensionId || !targetAgencyId || isSaving}
              >
                {isSaving ? 'Linking...' : 'Link this number'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={unlinking !== null}
        onOpenChange={open => !open && setUnlinking(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {unlinking?.mapping.is_active
                ? 'Unlink this fax number?'
                : 'Relink this fax number?'}
            </DialogTitle>
            <DialogDescription>
              {unlinking?.mapping.is_active && outboundCheck === 'live'
                ? `${routeOf(unlinking.mapping)} is ${unlinking.agencyName}'s Telnyx outbound number, and its order faxes go out through Telnyx. Unlinking it is refused until its outbound number is changed in the Telnyx Fax Lines panel.`
                : unlinking?.mapping.is_active
                  ? `Faxes arriving on ${routeOf(unlinking.mapping)} stop reaching ${unlinking.agencyName} and park in Unassigned Faxes until somebody places them. Faxes already received stay where they are.`
                  : unlinking
                    ? `Faxes arriving on ${routeOf(unlinking.mapping)} reach ${unlinking.agencyName} again. This fails if another mapping has taken that extension or number in the meantime.`
                    : ''}
            </DialogDescription>
          </DialogHeader>
          {unlinking?.mapping.is_active && outboundCheck === 'unknown' && (
            <p className='text-sm text-destructive'>
              Could not check whether this is {unlinking.agencyName}&apos;s live
              Telnyx outbound number. If it is, unlinking it is refused until
              its outbound number is changed in the Telnyx Fax Lines panel.
            </p>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setUnlinking(null)}>
              Cancel
            </Button>
            <Button
              variant={unlinking?.mapping.is_active ? 'destructive' : undefined}
              onClick={() => void confirmUnlink()}
              disabled={
                isUnlinking ||
                outboundCheck === 'checking' ||
                outboundCheck === 'live'
              }
            >
              {unlinking?.mapping.is_active
                ? isUnlinking
                  ? 'Unlinking...'
                  : 'Unlink'
                : isUnlinking
                  ? 'Relinking...'
                  : 'Relink'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDirectOpen} onOpenChange={setIsDirectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Direct fax</DialogTitle>
            <DialogDescription>
              Puts a PDF straight into an inbox, with no fax line involved, so
              the inbox can be used on a stage the fax provider does not deliver
              to. It arrives exactly as a real fax would. Refused in production.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <VirtualizedFormSelect
              label='Agency'
              placeholder='Leave empty to park it in Unassigned Faxes'
              options={allAgencies.map(agency => ({
                value: agency.id,
                label: agency.name,
              }))}
              sortOptions
              isClearable
              defaultValue={directAgencyId}
              onChange={event => {
                setDirectAgencyId(event.target.value);
                setDirectClinicId('');
                setDirectClinics([]);
                // The clinic list belongs to the agency, so it follows the
                // choice rather than lingering from the previous one.
                if (event.target.value) {
                  void listAgencyClinics(event.target.value)
                    .then(setDirectClinics)
                    .catch((error: unknown) => {
                      console.error('Failed to load clinics:', error);
                    });
                }
              }}
            />
            {directClinics.length > 0 && (
              <VirtualizedFormSelect
                key={`direct-clinic-${directAgencyId}`}
                label='Clinic (optional)'
                placeholder='Whole agency'
                options={directClinics.map(clinic => ({
                  value: clinic.id,
                  label: clinic.name,
                }))}
                sortOptions
                isClearable
                defaultValue={directClinicId}
                onChange={event => setDirectClinicId(event.target.value)}
              />
            )}
            <Input
              aria-label='Sender fax number'
              placeholder='Sender, E.164 (optional)'
              value={directFrom}
              onChange={event => setDirectFrom(event.target.value)}
            />
            <Input
              aria-label='Subject'
              placeholder='Subject (optional)'
              value={directSubject}
              onChange={event => setDirectSubject(event.target.value)}
            />
            {/* Without this a parked fax is reachable by neither the queue's
                extension column nor the offer to collect a backlog, both of
                which key on it. */}
            <Input
              aria-label='Arrived on extension'
              placeholder='Arrived on extension, digits only (optional)'
              value={directExtension}
              onChange={event => setDirectExtension(event.target.value)}
            />
            {/* Several at once, because one fax can carry a packet: they
                become one transmission with an attachment each, in this
                order, rather than several unrelated faxes in the inbox. */}
            <Input
              aria-label='PDFs to deliver'
              type='file'
              multiple
              accept='application/pdf,.pdf'
              onChange={event =>
                setDirectFiles(Array.from(event.target.files ?? []))
              }
            />
            {directFiles.length > 1 && (
              <p className='text-sm text-muted-foreground'>
                {directFiles.length} documents will arrive as one fax, in this
                order: {directFiles.map(file => file.name).join(', ')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsDirectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirmDirect()}
              disabled={directFiles.length === 0 || isDelivering}
            >
              {isDelivering ? 'Placing...' : 'Place in inbox'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSendOpen} onOpenChange={setIsSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send a fax</DialogTitle>
            <DialogDescription>
              Faxes one PDF to one number, from whichever line these credentials
              own. This dials a real telephone number, so check it before
              sending.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <Input
              aria-label='Fax number'
              placeholder='+14155550123'
              value={sendTo}
              onChange={event => setSendTo(event.target.value)}
            />
            {/* Several at once, because a packet is a cover sheet, an order
                and a face to face: sent one at a time they arrive as separate
                faxes the far end has to work out belong together. */}
            <Input
              aria-label='PDFs'
              type='file'
              multiple
              accept='application/pdf,.pdf'
              onChange={event =>
                setSendFiles(Array.from(event.target.files ?? []))
              }
            />
            {sendFiles.length > 1 && (
              <p className='text-sm text-muted-foreground'>
                {sendFiles.length} documents will go as one fax, in this order:{' '}
                {sendFiles.map(file => file.name).join(', ')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsSendOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirmSend()}
              disabled={!canSend || isSending}
            >
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FaxNumbersPanel;
